import { ThemeProvider, ToastProvider } from "@woba/ds-web";
import { Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ErrorContextProvider } from "./_old/error-boundary-context";
import { PageContainer } from "./_old/layouts/page-container";
import { ErrorBoundary } from "./_old/components/error-boundary";
import { ModalErrorRequest } from "./_old/layouts/modal-error-request";
import { AnalyticsProvider } from "./_old/features/analytics";
import { ErrorAnalyticsProvider } from "./_old/features/analytics/contexts/segment/error-segment";
import { DemoWarningBanner } from "./_old/features/demo";
import flagsmith from "flagsmith";
import { FlagsmithProvider } from "flagsmith/react";
import LoadingIndicator from "./_old/layouts/loading-indicator/loading-indicator";
import { Toaster } from "./core/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ChatbotProvider } from "./core/components/floating-chatbot/context/chatbot-context";
import { REPORT_SCHEDULE_CHAT_SLOTS as CHAT_SLOTS } from "./insights/components/report-schedule/chat";
import { ExperimentsProvider } from "./core/context/experiment-context/ExperimentsContext";
import { AppShell } from "./core/components/layout/app-shell";
import { AuthProvider } from "./core/components/auth/view/auth.provider";
import { useWosShell } from "./core/hooks/useWosShell";
import { ROUTES } from "./core/routes/constants";

const queryClient = new QueryClient();

function AppContent() {
  const isWosShell = useWosShell();
  const { pathname } = useLocation();
  const isActionCenter = pathname.startsWith(ROUTES.ACTION_CENTER);

  // No shell WOS as páginas vivem num contêiner arredondado (#FAFAFA, raio 32)
  // sobre o fundo #F1F1ED; o Centro de ações tem layout próprio de página cheia.
  const wrapInRoundedContainer = isWosShell && !isActionCenter;

  return (
    <ExperimentsProvider>
      {/* No shell WOS o chat vive num painel lateral docked (PED-12262 Fase 3);
          no shell legado permanece a bolha flutuante. */}
      <ChatbotProvider
        host={isWosShell ? "docked" : "floating"}
        slots={CHAT_SLOTS}
      >
        {wrapInRoundedContainer ? (
          <div className="relative h-full min-h-0 p-3 md:py-6">
            {/* O contêiner recorta os cantos; a rolagem fica no filho, com a barra
                fina e afastada dos cantos para ficar dentro do cartão. */}
            <div className="h-full overflow-hidden rounded-[32px] bg-white">
              <div className="h-full overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:my-6 [&::-webkit-scrollbar-track]:bg-transparent">
                <Suspense fallback={<LoadingIndicator />}>
                  <Outlet />
                </Suspense>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{ position: "relative" }}
            className={
              isWosShell && isActionCenter
                ? "flex min-h-0 flex-1 flex-col"
                : undefined
            }
          >
            <Suspense fallback={<LoadingIndicator />}>
              <Outlet />
            </Suspense>
          </div>
        )}
      </ChatbotProvider>
    </ExperimentsProvider>
  );
}

/**
 * Alterna entre o shell legado (PageContainer) e o shell WOS
 * (PED-12262) via feature flag `wos-manager-shell`. O PageContainer embute
 * os providers de auth/analytics/erro; no shell novo eles são compostos
 * aqui explicitamente para manter paridade.
 */
function ShellSwitch() {
  const isWosShell = useWosShell();

  if (!isWosShell) {
    return (
      <PageContainer>
        <AppContent />
      </PageContainer>
    );
  }

  return (
    <ErrorAnalyticsProvider>
      <ErrorBoundary fallback={(error) => <ModalErrorRequest error={error} />}>
        <AuthProvider>
          <AnalyticsProvider>
            <DemoWarningBanner />
            <AppShell>
              <AppContent />
            </AppShell>
          </AnalyticsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ErrorAnalyticsProvider>
  );
}

export const App = () => {
  return (
    <FlagsmithProvider
      options={{
        environmentID: import.meta.env.VITE_FLAGSMITH_ENVIRONMENT_ID,
        api: import.meta.env.VITE_FLAGSMITH_API_URL,
      }}
      flagsmith={flagsmith}
    >
      <ThemeProvider>
        <ToastContainer position="top-center" autoClose={2000} />
        <Toaster duration={3000} position="top-right" />
        <ToastProvider>
          <ErrorContextProvider>
            <QueryClientProvider client={queryClient}>
              <ShellSwitch />
            </QueryClientProvider>
          </ErrorContextProvider>
        </ToastProvider>
      </ThemeProvider>
    </FlagsmithProvider>
  );
};
